import { useState } from "react";
import { SectionTitle, Body, SecondaryBody } from "@sprout/typography";
import { PrimaryButton, SecondaryButton } from "@sprout/buttons";
import Divider from "@sprout/divider.tsx";
import CheckMarkCircleIcon from "@sprout/icons/check_mark_circle";
import CompanyLogo from "@components/company_logo.tsx";
import useTranslation from "@hooks/use_translation.ts";
import { useTimeAgo, composeStatusLabel } from "@utils/time_ago";
import { ConfirmationRecommendations } from "@components/job_post/confirmation_recommendations.tsx";
import { router } from "@inertiajs/react";
import type { JobRecommendation } from "@components/dashboard/job_recommendation_card.tsx";
import type { JobPost, SearchSpecificFields } from "@/types/jben/job_post.ts";

interface Props {
  jobPost: JobPost & SearchSpecificFields;
  onViewJobs: () => void;
  onSelectRecommendation?: (job: JobRecommendation) => void;
}

export const ApplicationSuccess = ({ jobPost, onViewJobs, onSelectRecommendation }: Props) => {
  const { t } = useTranslation("jobSearch");
  const [appliedAt] = useState(() => new Date());
  const appliedLabel = composeStatusLabel(t("applied"), useTimeAgo(appliedAt));

  return (
    <div className="flex flex-col items-start gap-4">
      <div className="flex items-center gap-3 pr-10">
        <CheckMarkCircleIcon size="lg" viewBox="0 0 16 16" color="green" ariaHidden />
        <SectionTitle large>{t("applicationSuccess.headerApplicationIn")}</SectionTitle>
      </div>
      <Body>{t("applicationSuccess.bodyForRecommendations")}</Body>
      <div className="w-full overflow-hidden border border-[var(--object-strokes-dividers,#ccd4d1)] rounded-lg">
        <div className="flex items-center gap-3 p-4">
          <CompanyLogo companyName={jobPost.company_name} logoUrl={jobPost.logo_url} size="small" />
          <div className="flex flex-col min-w-0 flex-1">
            <p className="font-medium text-base leading-6 text-[var(--text-primary,#15372c)] truncate">
              {jobPost.title}
            </p>
            <p className="text-sm leading-5 text-[var(--text-primary,#15372c)] truncate">
              {jobPost.company_name}
            </p>
          </div>
          <SecondaryButton onClick={() => router.visit("/applications")}>
            {t("applicationSuccess.viewApplication")}
          </SecondaryButton>
        </div>
        <div className="px-4 py-2 bg-[var(--object-fills-grey)]">
          <SecondaryBody>{appliedLabel}</SecondaryBody>
        </div>
      </div>
      <ConfirmationRecommendations
        appliedJobPostId={jobPost.id}
        onSelect={onSelectRecommendation}
      />
      <div className="-mx-8 w-[calc(100%+4rem)]">
        <Divider />
      </div>
      <div className="flex flex-row justify-end gap-3 w-full">
        <PrimaryButton onClick={onViewJobs}>{t("applicationSuccess.browseMoreJobs")}</PrimaryButton>
      </div>
    </div>
  );
};
